import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Map as ImmutableMap } from 'immutable';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { selectOpponent, selectPlayer } from '../ActiveGame/selectors';
import CardFace from '../Card/CardFace';
import { isCardPlace, waitsForLanding } from './anchorNames';
import { cardMoves } from './cardMoves';
import { takeDrop } from '../Drag/dropTargets';
import { snapshot } from './snapshot';

/** How long one card takes to fly, and how far apart cards that move together leave. */
const FLIGHT_MS = 450;
/** A card that stops on a Pokémon on the way (Potion) rests there this long before going on. */
const VIA_REST_MS = 380;
const STAGGER_MS = 90;
/** Flying cards pass over the board. */
const FLIGHT_Z = 40;
/** An energy card shrinking into its symbol takes a little longer, so the shrink can be seen. */
const INTO_SYMBOL_MS = 620;
/** How the symbol settles once the card has turned into it: a little bigger, back to its size. */
const SYMBOL_POP = [{ transform: 'scale(1.7)' }, { transform: 'scale(1)' }];
const SYMBOL_POP_MS = 240;
/** When a card in flight checks again where it's going, after it takes off. */
const RETARGET_AFTER_MS = [30, 120, 260];

/**
 * How far an element is turned on screen, in degrees: the mat draws the opponent's half upside down (Board/styled
 * Half), so their cards face them.
 */
const turns = new WeakMap();
function turnOf(element) {
  // Only switching between mat and table turns anything, and that draws new elements: remember each one's.
  if (turns.has(element)) {
    return turns.get(element);
  }
  let degrees = 0;
  for (let node = element; node && node !== document.body; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.transform && style.transform !== 'none') {
      const matrix = new DOMMatrixReadOnly(style.transform);
      degrees += Math.atan2(matrix.b, matrix.a) * (180 / Math.PI);
    }
  }
  const turn = Math.round(degrees) % 360;
  turns.set(element, turn);
  return turn;
}

/** Every marked place on screen (Anchor): where it is now and how it's turned, by name. */
function measureAnchors() {
  const places = new Map();
  document.querySelectorAll('[data-anchor]').forEach((anchor) => {
    const box = anchor.firstElementChild;
    if (box) {
      const { left, top, width, height } = box.getBoundingClientRect();
      places.set(anchor.dataset.anchor, { left, top, width, height, turn: turnOf(box) });
    }
  });
  return places;
}

/** What's shown at a place: the card there (anchors mark it by wrapping it). */
const boxAt = (name) => document.querySelector(`[data-anchor="${CSS.escape(name)}"]`)?.firstElementChild;

/** The symbols of a Pokémon's newest energy cards, the last `count` of them (CardFace draws them in attached order). */
const newestEnergy = (box, count) => (count > 0 ? [...box.querySelectorAll('[data-energy-attachment]')].slice(-count) : []);

/**
 * Hides or shows what a flying card is headed for until it lands; counts, so overlapping flights don't show it
 * early. A card that lands on top hides the card at its destination. An energy card hides only the newest energy
 * symbols on its Pokémon, since it turns into one of them as it arrives.
 *
 * @returns {number} how many cards are on their way there now
 */
const waiting = new Map();
function wait(name, becomesSymbol, delta) {
  const key = `${name}|${becomesSymbol}`;
  const count = (waiting.get(key) || 0) + delta;
  waiting.set(key, count);
  const box = boxAt(name);
  if (!box) {
    return count;
  }
  if (!becomesSymbol) {
    box.style.visibility = count > 0 ? 'hidden' : '';
    return count;
  }
  box.querySelectorAll('[data-energy-attachment]').forEach((group) => { group.style.visibility = ''; });
  newestEnergy(box, count).forEach((group) => { group.style.visibility = 'hidden'; });
  return count;
}

/**
 * The energy symbol an energy card on its way becomes: of the Pokémon's newest symbols still waiting, the one this
 * card is for (`waitingCount` counts it). A symbol is laid out though hidden, so where it will show can be measured.
 */
function symbolFor(toName, waitingCount) {
  const groups = [...(boxAt(toName)?.querySelectorAll('[data-energy-attachment]') || [])];
  const group = groups[groups.length - waitingCount];
  // The group is display: contents (it has no box of its own); its first symbol does.
  return group ? group.firstElementChild : null;
}

/** One card flying from one place to another, then gone. The card already at the destination waits for it. */
const Flight = ({ flight, onLanded }) => {
  const ref = useRef(null);
  const landedRef = useRef(onLanded);
  landedRef.current = onLanded;
  const { from, to, card, delay, toName, becomesSymbol, viaName } = flight;

  useLayoutEffect(() => {
    const hides = becomesSymbol || waitsForLanding(toName);
    let shown = false;
    let symbol = null;
    const show = () => {
      if (hides && !shown) {
        shown = true;
        wait(toName, becomesSymbol, -1);
        if (symbol) {
          symbol.animate(SYMBOL_POP, { duration: SYMBOL_POP_MS, easing: 'ease-out' });
        }
      }
    };
    const waitingCount = hides ? wait(toName, becomesSymbol, 1) : 0;

    // An energy card flies to where its symbol will be and shrinks into it; anything else lands on its place.
    symbol = becomesSymbol ? symbolFor(toName, waitingCount) : null;
    const landsOnCard = isCardPlace(toName);
    // From the flying card's own middle: the place it left can be a little bigger than the card drawn in it.
    const flying = ref.current.getBoundingClientRect();
    const travel = 'cubic-bezier(.2, .7, .2, 1)';
    const start = `translate(0, 0) scale(1) rotate(${from.turn}deg)`;

    // Where it's headed, as the screen is now. The board can still move after a card takes off (a banner that the
    // move ends goes away, and the board takes its room), so this is asked again on the way.
    const keyframes = () => {
      const symbolBox = symbol && symbol.getBoundingClientRect();
      const place = boxAt(toName);
      const target = symbolBox && symbolBox.width ? symbolBox : (place ? place.getBoundingClientRect() : to);
      const scale = symbolBox && symbolBox.width
        ? symbolBox.width / flying.width
        : (landsOnCard ? target.width / flying.width : 0.4);
      const dx = target.left + target.width / 2 - (flying.left + flying.width / 2);
      const dy = target.top + target.height / 2 - (flying.top + flying.height / 2);
      const end = `translate(${dx}px, ${dy}px) scale(${scale}) rotate(${to.turn}deg)`;
      // A stop on a Pokémon on the way (Potion): there at full size, a moment on it, then on to where it ends.
      const viaPlace = viaName && boxAt(viaName);
      if (viaPlace) {
        const stop = viaPlace.getBoundingClientRect();
        const vx = stop.left + stop.width / 2 - (flying.left + flying.width / 2);
        const vy = stop.top + stop.height / 2 - (flying.top + flying.height / 2);
        const at = `translate(${vx}px, ${vy}px) scale(${stop.width / flying.width}) rotate(${turnOf(viaPlace)}deg)`;
        const total = FLIGHT_MS * 2 + VIA_REST_MS;
        return [
          { transform: start, opacity: 1, easing: travel },
          { transform: at, opacity: 1, offset: FLIGHT_MS / total },
          { transform: at, opacity: 1, offset: (FLIGHT_MS + VIA_REST_MS) / total, easing: travel },
          { transform: end, opacity: landsOnCard ? 1 : 0 },
        ];
      }
      // A card turns on the way between places that face different players (the opponent's half of the mat). An
      // energy card travels, then fades into its symbol: the easing is the travel's, so the fade keeps to the end.
      return becomesSymbol ? [
        { transform: start, opacity: 1, easing: travel },
        { transform: end, opacity: 1, offset: 0.75 },
        { transform: end, opacity: 0 },
      ] : [
        { transform: start, opacity: 1 },
        { transform: end, opacity: landsOnCard ? 1 : 0 },
      ];
    };
    const duration = becomesSymbol ? INTO_SYMBOL_MS : (viaName ? FLIGHT_MS * 2 + VIA_REST_MS : FLIGHT_MS);
    const animation = ref.current.animate(keyframes(), {
      duration, delay, easing: becomesSymbol || viaName ? 'linear' : travel, fill: 'both',
    });
    const retargets = RETARGET_AFTER_MS.map(ms => setTimeout(() => animation.effect.setKeyframes(keyframes()), ms));
    animation.onfinish = () => {
      show();
      landedRef.current();
    };
    return () => {
      retargets.forEach(clearTimeout);
      animation.cancel();
      show();
    };
  }, [from, to, delay, toName, becomesSymbol, viaName]);

  return (
    <div
      ref={ref}
      style={{ position: 'fixed', left: from.left, top: from.top, zIndex: FLIGHT_Z, pointerEvents: 'none', '--card-w': `${from.width}px` }}
      aria-hidden="true"
    >
      <CardFace card={card || undefined} />
    </div>
  );
};

Flight.propTypes = {
  flight: PropTypes.shape({
    from: PropTypes.object.isRequired,
    to: PropTypes.object.isRequired,
    card: PropTypes.instanceOf(ImmutableMap),
    delay: PropTypes.number.isRequired,
    toName: PropTypes.string.isRequired,
    becomesSymbol: PropTypes.bool.isRequired,
    viaName: PropTypes.string,
  }).isRequired,
  onLanded: PropTypes.func.isRequired,
};

/**
 * Cards flying across the board as things happen, yours and the opponent's: a Pokémon moving up from the Bench, a
 * card drawn into the hand, energy attached, a knocked-out Pokémon going to the discard pile. The moves are read
 * from what changed (cardMoves); where they fly from is where things were on screen before the change. Nothing flies
 * for people who ask their device for less motion.
 */
const FlyingCards = () => {
  const player = useSelector(selectPlayer);
  const opponent = useSelector(selectOpponent);
  const before = useRef(null);
  const places = useRef(new Map());
  const [flying, setFlying] = useState([]);

  useLayoutEffect(() => {
    const now = [snapshot(player), snapshot(opponent)];
    const was = before.current;
    before.current = now;
    const fresh = measureAnchors();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (was && !reduced) {
      const moves = [...cardMoves(was[0], now[0]), ...cardMoves(was[1], now[1])];
      // A card you dragged is already where you let it go: it doesn't fly there again. One that stops on the way
      // (a Potion dropped on its Pokémon) goes on from there.
      const launches = moves
        .map(move => (takeDrop(move.from) ? (move.via ? { ...move, from: move.via, via: null } : null) : move))
        .filter(Boolean)
        .map((move, i) => ({
          ...move, key: `${Date.now()}-${i}`, from: places.current.get(move.from), to: fresh.get(move.to), toName: move.to,
          viaName: move.via || undefined, delay: i * STAGGER_MS,
        }))
        .filter(launch => launch.from && launch.to);

      if (launches.length) {
        setFlying(current => [...current, ...launches]);
      }
    }

    places.current = fresh;
  }, [player, opponent]);

  // Where things are moves when the page scrolls or resizes; keep up, so cards leave from the right place.
  useEffect(() => {
    let frame = null;
    const remeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { places.current = measureAnchors(); });
    };
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
      cancelAnimationFrame(frame);
    };
  }, []);

  const landed = (launch) => () => setFlying(current => current.filter(flight => flight.key !== launch.key));

  return createPortal(
    flying.map(launch => <Flight key={launch.key} flight={launch} onLanded={landed(launch)} />),
    document.body,
  );
};

export default FlyingCards;
